import { DeleteOutlined, HistoryOutlined } from '@mui/icons-material';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material';
import { generateClient, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useState } from 'react';
import {
	createScheduledBulkOperation,
	deleteScheduledBulkOperation,
	updateScheduledBulkOperation,
} from 'src/graphql/mutations';
import { scheduledBulkOperationsByTenant } from 'src/graphql/queries';
import { tabStyle } from 'src/theme';
import { UserContext } from '../../contexts/UserContext';
import {
	buildScheduleAuditInput,
	writeScheduleAuditLog,
} from '../../tools/scheduleAudit';
import { ConfirmDialog } from '../confirmDialog/confirmDialog';
// Circular by module graph (runHistoryDialog imports `runStatusColors` from
// here), but safe: each side only reads the other's binding while rendering,
// never during module evaluation.
import { RunHistoryDialog } from './runHistoryDialog';

const dayLabels = [
	{ value: 0, label: 'SU.' },
	{ value: 1, label: 'MO.' },
	{ value: 2, label: 'TU.' },
	{ value: 3, label: 'WE.' },
	{ value: 4, label: 'TH.' },
	{ value: 5, label: 'FR.' },
	{ value: 6, label: 'SA.' },
];

/**
 * Timing guidance shown with the schedule form.
 *
 * A run replays its stored query across the whole contact inventory, so two
 * operations firing at the same time — or one firing while the dialer is
 * working the same records — contend for the same read and write capacity and
 * every one of them takes longer. Nothing enforces the spacing, which is why
 * this is asked for here, next to the time the operator is choosing, and points
 * at the tab that lists the times already taken.
 */
export const SCHEDULE_TIMING_GUIDANCE =
	'For best performance, pick a time outside your calling hours and offset it from your other scheduled operations. Runs that overlap compete for the same capacity, so all of them take longer. Check the Scheduled Operations tab for the times already in use.';

/**
 * Maps a run status onto a MUI chip color. Exported so the run history dialog
 * renders the same status colors as the operations list.
 * @type {Record<string, 'success'|'warning'|'error'|'info'|'default'>}
 */
export const runStatusColors = {
	Success: 'success',
	Partial: 'warning',
	Failed: 'error',
	Running: 'info',
	Skipped: 'default',
};

/**
 * Formats a stored schedule into a readable summary, e.g. "08:30 on Mon, Wed".
 * @param {{time: string, days: number[], timeZone: string}} schedule
 * @returns {string}
 */
function describeSchedule(schedule) {
	if (!schedule) {
		return '--';
	}

	const days = (schedule.days || [])
		.slice()
		.sort((a, b) => a - b)
		.map((day) => moment().day(day).format('ddd'))
		.join(', ');

	return `${schedule.time} on ${days || 'no days'}`;
}

/**
 * Dialog for creating and managing recurring bulk edit / bulk delete operations.
 *
 * The query currently built on the Bulk Edit page is stored alongside the action,
 * then an EventBridge Scheduler schedule is attached to it so it replays on a
 * weekly cadence.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object} props.qbQuery Raw query builder state, used to rehydrate the UI later.
 * @param {() => object} props.getSearchFilter Returns the compiled OpenSearch filter.
 * @param {'Update'|'Delete'} props.action
 * @param {string} [props.updateField]
 * @param {any} [props.updateValue] Type coerced value that will be written.
 * @param {any} [props.updateValueRaw] Raw form value, kept so the UI can rehydrate.
 * @param {number} [props.matchedCount] Contacts currently matching the query.
 * @param {{id: string, name: string}} [props.sourceSegment] Segment the current query
 *   was loaded from, if any. Recorded on the operation at creation time as a reference
 *   value only; the executor always replays the stored search filter.
 * @category Components
 * @component
 */
export function BulkScheduleDialog({
	open,
	onClose,
	qbQuery,
	getSearchFilter,
	action,
	updateField,
	updateValue,
	updateValueRaw,
	matchedCount,
	sourceSegment,
}) {
	const client = generateClient();
	const userContext = useContext(UserContext);

	const [tab, setTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [operations, setOperations] = useState([]);
	const [toDelete, setToDelete] = useState(null);
	// Operation whose run history is open, or null when the history dialog is closed.
	const [historyOperation, setHistoryOperation] = useState(null);
	const [newSchedule, setNewSchedule] = useState({
		name: '',
		description: '',
		time: '08:00',
		days: [],
		timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
	});

	const loadOperations = useCallback(async () => {
		if (!userContext.tenantId) {
			return;
		}

		setLoading(true);
		try {
			const result = await client.graphql({
				query: scheduledBulkOperationsByTenant,
				variables: { tenant: userContext.tenantId, limit: 200 },
			});
			setOperations(result.data.scheduledBulkOperationsByTenant.items || []);
		} catch (err) {
			console.error('Error loading scheduled bulk operations', err);
			enqueueSnackbar('Unable to load scheduled operations', {
				variant: 'error',
			});
		}
		setLoading(false);
	}, [userContext.tenantId]);

	useEffect(() => {
		if (open) {
			loadOperations();
		}
	}, [open, loadOperations]);

	/**
	 * Calls the schedule management route on the cdyxbulkedit lambda.
	 * @param {object} body
	 */
	async function callScheduleApi(body) {
		const session = await fetchAuthSession();
		const response = await post({
			apiName: 'cdyxoutreach',
			path: '/bulk-edit/schedule',
			options: {
				headers: {
					Authorization: `Bearer ${session.tokens.idToken}`,
					'x-api-key': userContext.apiKey,
				},
				body,
			},
		}).response;

		return response.body.json();
	}

	function resetNewSchedule() {
		setNewSchedule({
			name: '',
			description: '',
			time: '08:00',
			days: [],
			timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		});
	}

	async function saveSchedule() {
		const rules = qbQuery?.rules ?? [];

		if (!newSchedule.name.trim()) {
			enqueueSnackbar('Please give this scheduled operation a name', {
				variant: 'error',
			});
			return;
		}

		if (rules.length === 0) {
			enqueueSnackbar('Build a query before scheduling an operation', {
				variant: 'error',
			});
			return;
		}

		if (newSchedule.days.length === 0) {
			enqueueSnackbar('Select at least one day of the week', {
				variant: 'error',
			});
			return;
		}

		if (action === 'Update' && !updateField) {
			enqueueSnackbar('Choose the field to update before scheduling', {
				variant: 'error',
			});
			return;
		}

		setSaving(true);
		let createdId = null;

		try {
			const input = {
				tenant: userContext.tenantId,
				name: newSchedule.name.trim(),
				description: newSchedule.description.trim() || null,
				action,
				query: {
					qbQuery: JSON.stringify(qbQuery),
					searchFilter: JSON.stringify(getSearchFilter()),
				},
				schedule: {
					time: newSchedule.time,
					days: newSchedule.days.slice().sort((a, b) => a - b),
					timeZone: newSchedule.timeZone,
				},
				enabled: true,
				// Reference values only, stamped at creation and never re-stamped:
				// the executor replays query.searchFilter, not the segment.
				sourceSegmentId: sourceSegment?.id ?? null,
				sourceSegmentName: sourceSegment?.name ?? null,
				createdBy: userContext.email,
				updatedBy: userContext.email,
			};

			if (action === 'Update') {
				input.updateField = updateField;
				// Stored as AWSJSON so numbers and booleans survive the round trip.
				input.updateValue = JSON.stringify(updateValue ?? null);
				input.updateValueRaw =
					updateValueRaw === undefined || updateValueRaw === null
						? null
						: String(updateValueRaw);
			}

			const created = await client.graphql({
				query: createScheduledBulkOperation,
				variables: { input },
			});

			const createdOperation = created.data.createScheduledBulkOperation;
			createdId = createdOperation.id;

			await callScheduleApi({ operationId: createdId });

			// Audited only once the schedule is attached, so the rollback path below
			// leaves no entry behind for an operation that no longer exists. The
			// result is ignored on purpose: writeScheduleAuditLog never rejects and a
			// failed entry must not undo the operation that was just created.
			await writeScheduleAuditLog(
				client,
				buildScheduleAuditInput({
					tenant: userContext.tenantId,
					userName: userContext.email,
					documentId: createdId,
					operation: { ...input, ...createdOperation },
					action: 'CREATED',
				}),
			);

			enqueueSnackbar('Scheduled operation created', { variant: 'success' });
			resetNewSchedule();
			await loadOperations();
			setTab(1);
		} catch (err) {
			console.error('Error creating scheduled bulk operation', err);
			enqueueSnackbar('Unable to create the scheduled operation', {
				variant: 'error',
			});

			// Don't leave a stored operation behind that has no schedule attached.
			if (createdId) {
				try {
					await client.graphql({
						query: deleteScheduledBulkOperation,
						variables: { input: { id: createdId } },
					});
				} catch (cleanupErr) {
					console.error(
						'Failed to clean up the partially created operation',
						cleanupErr,
					);
				}
			}
		}

		setSaving(false);
	}

	async function toggleEnabled(operation, enabled) {
		setSaving(true);
		try {
			await client.graphql({
				query: updateScheduledBulkOperation,
				variables: {
					input: {
						id: operation.id,
						enabled,
						updatedBy: userContext.email,
					},
				},
			});

			await callScheduleApi({ operationId: operation.id, enabled });

			setOperations((current) =>
				current.map((item) =>
					item.id === operation.id ? { ...item, enabled } : item,
				),
			);

			// Post-change object, so the snapshot records the resulting `enabled`
			// value. Any configuration form added later audits itself by calling
			// these same two helpers with its own post-change operation.
			await writeScheduleAuditLog(
				client,
				buildScheduleAuditInput({
					tenant: userContext.tenantId,
					userName: userContext.email,
					documentId: operation.id,
					operation: { ...operation, enabled },
					action: 'UPDATED',
				}),
			);

			enqueueSnackbar(
				enabled ? 'Scheduled operation resumed' : 'Scheduled operation paused',
				{ variant: 'success' },
			);
		} catch (err) {
			console.error('Error updating scheduled bulk operation', err);
			enqueueSnackbar('Unable to update the scheduled operation', {
				variant: 'error',
			});
			await loadOperations();
		}
		setSaving(false);
	}

	async function confirmDelete() {
		const operation = toDelete;
		setToDelete(null);

		if (!operation) {
			return;
		}

		setSaving(true);
		try {
			// Remove the schedule first so a failure can't orphan a live trigger.
			await callScheduleApi({ operationId: operation.id, delete: true });

			await client.graphql({
				query: deleteScheduledBulkOperation,
				variables: { input: { id: operation.id } },
			});

			setOperations((current) =>
				current.filter((item) => item.id !== operation.id),
			);

			// DELETED entries carry `currentValue: '{}'`: there is no post-change
			// configuration left to snapshot.
			await writeScheduleAuditLog(
				client,
				buildScheduleAuditInput({
					tenant: userContext.tenantId,
					userName: userContext.email,
					documentId: operation.id,
					operation,
					action: 'DELETED',
				}),
			);

			enqueueSnackbar('Scheduled operation deleted', { variant: 'success' });
		} catch (err) {
			console.error('Error deleting scheduled bulk operation', err);
			enqueueSnackbar('Unable to delete the scheduled operation', {
				variant: 'error',
			});
			await loadOperations();
		}
		setSaving(false);
	}

	const actionSummary =
		action === 'Delete'
			? 'Delete every contact matching the query'
			: `Set ${updateField || '(no field selected)'} to ${updateValueRaw === '' ||
				updateValueRaw === undefined ||
				updateValueRaw === null
				? '(empty)'
				: String(updateValueRaw)
			} on every contact matching the query`;

	return (
		<>
			<Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
				<DialogTitle>Recurring Bulk {action}</DialogTitle>
				{/* Between the title and the content, not inside it: DialogContent has
				    its own padding, so a strip placed within it cannot draw a rule to
				    the dialog's edges.

				    Also moves selection from per-Tab `onClick` to Tabs' `onChange`.
				    `onClick` only fires for pointer input, so the strip could not be
				    operated with the arrow keys. */}
				<Tabs
					value={tab}
					onChange={(event, value) => setTab(value)}
					indicatorColor="primary"
					textColor="primary"
					variant="fullWidth"
					sx={{ borderBottom: 1, borderColor: 'divider' }}
				>
					<Tab sx={tabStyle} label="New Schedule" value={0} />
					<Tab
						sx={tabStyle}
						label={`Scheduled Operations (${operations.length})`}
						value={1}
					/>
				</Tabs>
				<DialogContent>
					{tab === 0 && (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
								paddingTop: 2,
							}}
						>
							<Alert
								severity={action === 'Delete' ? 'warning' : 'info'}
								variant="outlined"
							>
								<Typography variant="body2">{actionSummary}.</Typography>
								<Typography variant="body2">
									The query is stored and re-run on each occurrence, so the
									contacts affected will be whatever matches at that time
									{typeof matchedCount === 'number'
										? ` (${matchedCount} match right now)`
										: ''}
									.
								</Typography>
							</Alert>

							<Card variant="outlined">
								<CardHeader
									title="Schedule"
									titleTypographyProps={{
										variant: 'body2',
										color: 'textSecondary',
									}}
								/>
								<CardContent>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: 2,
										}}
									>
										<TextField
											label="Name"
											required
											value={newSchedule.name}
											onChange={(e) =>
												setNewSchedule({
													...newSchedule,
													name: e.target.value,
												})
											}
										/>
										<TextField
											label="Description"
											value={newSchedule.description}
											onChange={(e) =>
												setNewSchedule({
													...newSchedule,
													description: e.target.value,
												})
											}
										/>

										<ToggleButtonGroup
											value={newSchedule.days}
											onChange={(event, days) =>
												setNewSchedule({ ...newSchedule, days })
											}
										>
											{dayLabels.map((day) => (
												<ToggleButton key={day.value} value={day.value}>
													{day.label}
												</ToggleButton>
											))}
										</ToggleButtonGroup>

										<TextField
											variant="outlined"
											type="time"
											label="Time"
											value={newSchedule.time}
											onChange={(e) =>
												setNewSchedule({
													...newSchedule,
													time: e.target.value,
												})
											}
											InputLabelProps={{ shrink: true }}
										/>

										<Typography variant="body2" color="textSecondary">
											This schedule runs in the {newSchedule.timeZone} timezone.
										</Typography>

										{/* Sits with the day and time controls rather than in the
										    action summary at the top of the tab: it is advice about
										    the choice being made here, not about what the operation
										    will do. */}
										<Typography variant="body2" color="textSecondary">
											{SCHEDULE_TIMING_GUIDANCE}
										</Typography>
									</Box>
								</CardContent>
							</Card>
						</Box>
					)}

					{tab === 1 && (
						<Box sx={{ paddingTop: 2 }}>
							{loading && <CircularProgress variant="indeterminate" />}
							{!loading && operations.length === 0 && (
								<Typography variant="body2" color="textSecondary">
									No recurring bulk operations have been scheduled yet.
								</Typography>
							)}
							{!loading && operations.length > 0 && (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>Name</TableCell>
											<TableCell>Action</TableCell>
											<TableCell>Schedule</TableCell>
											<TableCell>Timezone</TableCell>
											<TableCell>Source Segment</TableCell>
											<TableCell>Last Run</TableCell>
											<TableCell>Enabled</TableCell>
											<TableCell align="right">History</TableCell>
											<TableCell align="right">Remove</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{operations.map((operation) => (
											<TableRow key={operation.id}>
												<TableCell>
													<Typography variant="body2">
														{operation.name}
													</Typography>
													{operation.description && (
														<Typography variant="caption" color="textSecondary">
															{operation.description}
														</Typography>
													)}
												</TableCell>
												<TableCell>
													{operation.action === 'Update'
														? `Update ${operation.updateField}`
														: 'Delete'}
												</TableCell>
												<TableCell>
													{describeSchedule(operation.schedule)}
												</TableCell>
												<TableCell>
													{operation.schedule?.timeZone ?? '--'}
												</TableCell>
												<TableCell>
													{operation.sourceSegmentName ?? '--'}
												</TableCell>
												<TableCell>
													{operation.lastRunAt ? (
														<Tooltip
															title={
																operation.lastRunMessage ||
																`${operation.lastRunProcessed ?? 0} of ${operation.lastRunMatched ?? 0
																} contacts processed`
															}
														>
															<Box>
																<Chip
																	size="small"
																	label={operation.lastRunStatus ?? 'Unknown'}
																	color={
																		runStatusColors[operation.lastRunStatus] ??
																		'default'
																	}
																/>
																<Typography
																	variant="caption"
																	color="textSecondary"
																	sx={{ display: 'block' }}
																>
																	{moment(operation.lastRunAt).format(
																		'MM/DD/YYYY hh:mm A',
																	)}
																</Typography>
															</Box>
														</Tooltip>
													) : (
														<Typography variant="caption" color="textSecondary">
															Never run
														</Typography>
													)}
												</TableCell>
												<TableCell>
													<FormControlLabel
														control={
															<Switch
																checked={operation.enabled !== false}
																disabled={saving}
																onChange={(e) =>
																	toggleEnabled(operation, e.target.checked)
																}
															/>
														}
														label=""
													/>
												</TableCell>
												<TableCell align="right">
													<Tooltip
														title={`View run history for ${operation.name}`}
													>
														<IconButton
															aria-label={`View run history for ${operation.name}`}
															onClick={() => setHistoryOperation(operation)}
														>
															<HistoryOutlined />
														</IconButton>
													</Tooltip>
												</TableCell>
												<TableCell align="right">
													<IconButton
														disabled={saving}
														onClick={() => setToDelete(operation)}
													>
														<DeleteOutlined />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Box sx={{ display: 'flex', gap: 2 }}>
						<Button variant="secondary" onClick={onClose}>
							Close
						</Button>
						{tab === 0 && (
							<Button
								variant="primary"
								disabled={saving}
								onClick={saveSchedule}
							>
								{saving ? 'Saving...' : `Schedule Bulk ${action}`}
							</Button>
						)}
					</Box>
				</DialogActions>
			</Dialog>

			<ConfirmDialog
				open={!!toDelete}
				title="Delete Scheduled Operation"
				description={`Remove "${toDelete?.name}"? The recurring schedule will be deleted. Contacts already processed are not affected.`}
				actionOneText="Delete"
				actionOneHandler={confirmDelete}
				actionTwoText="Cancel"
				actionTwoHandler={() => setToDelete(null)}
			/>

			<RunHistoryDialog
				open={!!historyOperation}
				operation={historyOperation}
				tenantId={userContext.tenantId}
				onClose={() => setHistoryOperation(null)}
			/>
		</>
	);
}
